import {
  call,
  takeEvery,
  takeLatest,
  select,
  put,
  fork,
  all,
} from 'redux-saga/effects'
import T from 'i18n-react'
import * as R from 'ramda'
import P from 'helpers/predicates'
import camelCase from 'lodash/camelCase'

import { watchValidations } from 'containers/Form/Validation/sagas'
import { watchRisks } from 'containers/Form/RiskValidation/sagas'

import {
  LOAD_REVISION,
} from './constants'
import {
  revisionEntitySelector,
  elementsEntitySelector,
} from './selectors'
import {
  loadRevisionSuccess,
  loadRevisionError,
} from './actions'

const createObject = R.compose(
  (val) => [val],
  R.reduce(
    (final, [property, value]) => R.assocPath(property, value, final),
    {}
  )
)

const createArray = R.compose(
  R.values,
  R.reduce(
    (final, [property, value]) => R.assocPath(property, value, final),
    {}
  )
)

const createValues = (paths) => {
  let values = []
  if (paths.length > 0) {
    if (paths[0][0] && paths[0][0].length >= 2) {
      values = createArray(paths)
    } else {
      values = createObject(paths)
    }
  }
  return values
}

/**
 * getElementPaths
 *
 * @param properties
 * @param revision
 * @returns {undefined}
 */
function getElementPaths(properties, revision, paths) {
  const handleObject = R.compose(
    R.reduce(
      (final, [segment, property]) => {
        let branches = getElementPaths(property, revision[segment], paths)
        if (branches.length === 0) return R.append([segment], final)
        else return R.concat(R.map(branch => [segment, ...branch])(branches), final)
      },
      []
    ),
    R.filter(([segment]) => P.isNotNil(revision[segment])),
    R.toPairs,
    R.prop('properties')
  )
  const handleArray = R.addIndex(R.reduce)((final, value, index) => {
    let branches = getElementPaths(properties.items, value, paths)
    return R.concat(R.map(branch => [index, ...branch])(branches), final)
  }, [])

  let newPaths = paths

  if (R.either(R.isNil, R.isEmpty)(revision))
    return newPaths

  if (properties.type === 'object')
    newPaths = handleObject(properties)
  else if (properties.type === 'array')
    newPaths = handleArray(revision)

  return newPaths
}

function getElementsData(key, properties, revision) {
  const propertyKey = camelCase(key)
  const paths = getElementPaths(properties, revision[propertyKey], [])
  const newPaths = R.map((path => [path, R.path([propertyKey, ...path], revision)]))(paths)
  return createValues(newPaths)
}

function getElements(revision, elements) {
  const newElements = elements.reduce((final, { id, key, values, ...rest }) => R.assoc(id, {
    ...rest,
    id,
    key,
    values: getElementsData(key, values, revision)
  }, final), {})
  return newElements
}

function* handleLoadRevision({ revisionId }) {
  try {
    const revisions = yield select(revisionEntitySelector)
    const { data: revision } = revisions[revisionId]
    const elementEntities = yield select(elementsEntitySelector)
    // console.log(elementEntities)
    const elements = getElements(revision, R.values(elementEntities))
    // console.log(elements)
    yield put(loadRevisionSuccess(revisionId, elements))
  } catch (error) {
    yield put(loadRevisionError(error))
  }
}

function* watchLoadRevision() {
  yield takeEvery(LOAD_REVISION, handleLoadRevision)
}

function* loadSagas() {
  yield all([
    fork(watchValidations),
    fork(watchRisks),
    fork(watchLoadRevision)
  ])
}

export default loadSagas
export {
  getElements
}